import { useEffect, useCallback, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  $createParagraphNode,
  $getRoot,
  EditorState,
  LexicalEditor,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  LexicalCommand,
  DecoratorNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
  DOMExportOutput,
  LexicalNode,
  $getNodeByKey,
  CLICK_COMMAND,
} from 'lexical';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

// Custom Image Node
export type SerializedImageNode = Spread<
  {
    src: string;
    altText: string;
    width: 'small' | 'medium' | 'large' | 'full';
  },
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;
  __width: 'small' | 'medium' | 'large' | 'full';

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__altText, node.__width, node.__key);
  }

  constructor(src: string, altText: string, width: 'small' | 'medium' | 'large' | 'full' = 'full', key?: NodeKey) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width;
  }

  createDOM(): HTMLElement {
    const div = document.createElement('div');
    div.className = 'my-4';
    return div;
  }

  updateDOM(): false {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    
    const widthClasses = {
      small: 'w-1/3',
      medium: 'w-2/3',
      large: 'w-5/6',
      full: 'w-full'
    };
    
    element.className = `${widthClasses[this.__width]} rounded-lg my-4 mx-auto`;
    return { element };
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { src, altText, width } = serializedNode;
    return $createImageNode({ src, altText, width });
  }

  exportJSON(): SerializedImageNode {
    return {
      src: this.__src,
      altText: this.__altText,
      width: this.__width,
      type: 'image',
      version: 1,
    };
  }

  setWidth(width: 'small' | 'medium' | 'large' | 'full'): void {
    const writable = this.getWritable();
    writable.__width = width;
  }

  decorate(): JSX.Element {
    return (
      <ImageComponent
        src={this.__src}
        altText={this.__altText}
        width={this.__width}
        nodeKey={this.getKey()}
      />
    );
  }
}

function ImageComponent({
  src,
  altText,
  width,
  nodeKey,
}: {
  src: string;
  altText: string;
  width: 'small' | 'medium' | 'large' | 'full';
  nodeKey: NodeKey;
}) {
  const [editor] = useLexicalComposerContext();
  const [showMenu, setShowMenu] = useState(false);

  const widthClasses = {
    small: 'w-1/3',
    medium: 'w-2/3',
    large: 'w-5/6',
    full: 'w-full'
  };

  const widthLabels = {
    small: 'Pequena (33%)',
    medium: 'Média (66%)',
    large: 'Grande (83%)',
    full: 'Largura Total (100%)'
  };

  const handleWidthChange = (newWidth: 'small' | 'medium' | 'large' | 'full') => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isImageNode(node)) {
        node.setWidth(newWidth);
      }
    });
    setShowMenu(false);
  };

  const handleDelete = () => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (node) {
        node.remove();
      }
    });
  };

  return (
    <div className="relative group my-4">
      <img
        src={src}
        alt={altText}
        className={`${widthClasses[width]} rounded-lg mx-auto`}
        style={{ maxWidth: '100%', height: 'auto' }}
      />
      
      {/* Overlay com botões */}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => setShowMenu(!showMenu)}
          className="bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-image-edit-line"></i>
          Redimensionar
        </button>
        <button
          type="button"
          onClick={handleDelete}
          className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-delete-bin-line"></i>
          Remover
        </button>
      </div>

      {/* Menu de tamanhos */}
      {showMenu && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-2xl p-4 z-10 min-w-[250px]">
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
            <h4 className="font-bold text-gray-900 text-sm">Tamanho da Imagem</h4>
            <button
              onClick={() => setShowMenu(false)}
              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-gray-600"></i>
            </button>
          </div>
          <div className="space-y-2">
            {(['small', 'medium', 'large', 'full'] as const).map((size) => (
              <button
                key={size}
                type="button"
                onClick={() => handleWidthChange(size)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                  width === size
                    ? 'bg-red-100 text-red-700 font-medium'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{widthLabels[size]}</span>
                  {width === size && <i className="ri-check-line text-red-600"></i>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function $createImageNode({ src, altText, width = 'full' }: { src: string; altText: string; width?: 'small' | 'medium' | 'large' | 'full' }): ImageNode {
  return new ImageNode(src, altText, width);
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}

const theme = {
  paragraph: 'mb-2',
  heading: {
    h1: 'text-3xl font-bold mb-4',
    h2: 'text-2xl font-bold mb-3',
    h3: 'text-xl font-bold mb-2',
  },
  list: {
    ul: 'list-disc ml-6 mb-2',
    ol: 'list-decimal ml-6 mb-2',
    listitem: 'mb-1',
  },
  quote: 'border-l-4 border-red-500 pl-4 italic text-gray-600 my-4',
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'line-through',
  },
  link: 'text-red-600 underline cursor-pointer',
};

const INSERT_IMAGE_COMMAND: LexicalCommand<string> = createCommand();

function ImagePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      INSERT_IMAGE_COMMAND,
      (payload: string) => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const imageNode = $createImageNode({
            src: payload,
            altText: 'Imagem do artigo',
            width: 'full',
          });
          selection.insertNodes([imageNode]);
        }
        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );
  }, [editor]);

  return null;
}

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
    }
  }, []);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatHeading = (headingSize: 'h1' | 'h2' | 'h3') => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      }
    });
  };

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatQuote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  };

  const handleImageUpload = async (file: File) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Tipo de ficheiro não suportado. Use JPG, PNG, GIF ou WebP.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Ficheiro muito grande. Máximo 5MB.');
      return;
    }

    setUploadingImage(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `uploads/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('blog-images').getPublicUrl(filePath);
      const publicUrl = data?.publicUrl;

      if (!publicUrl) throw new Error('Não foi possível obter a URL pública da imagem.');

      editor.dispatchCommand(INSERT_IMAGE_COMMAND, publicUrl);
      setShowImageModal(false);
      setImageUrl('');
    } catch (err) {
      console.error('Erro ao fazer upload:', err);
      alert('Erro ao fazer upload da imagem. Tente novamente.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleInsertImageUrl = () => {
    if (!imageUrl.trim()) {
      alert('Por favor, insira uma URL válida.');
      return;
    }
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, imageUrl);
    setShowImageModal(false);
    setImageUrl('');
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-1 p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        {/* Text Format */}
        <div className="flex items-center gap-1 pr-3 border-r border-gray-300">
          <button
            type="button"
            onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
            className={`w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer ${isBold ? 'bg-gray-200 text-red-600' : 'text-gray-700'}`}
            title="Negrito"
          >
            <i className="ri-bold"></i>
          </button>
          <button
            type="button"
            onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
            className={`w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer ${isItalic ? 'bg-gray-200 text-red-600' : 'text-gray-700'}`}
            title="Itálico"
          >
            <i className="ri-italic"></i>
          </button>
          <button
            type="button"
            onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
            className={`w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer ${isUnderline ? 'bg-gray-200 text-red-600' : 'text-gray-700'}`}
            title="Sublinhado"
          >
            <i className="ri-underline"></i>
          </button>
          <button
            type="button"
            onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer text-gray-700"
            title="Riscado"
          >
            <i className="ri-strikethrough"></i>
          </button>
        </div>

        {/* Headings */}
        <div className="flex items-center gap-1 px-3 border-r border-gray-300">
          <button
            type="button"
            onClick={() => formatParagraph()}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer text-gray-700 text-xs font-medium"
            title="Parágrafo"
          >
            P
          </button>
          <button
            type="button"
            onClick={() => formatHeading('h1')}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer text-gray-700"
            title="Título 1"
          >
            <i className="ri-h-1"></i>
          </button>
          <button
            type="button"
            onClick={() => formatHeading('h2')}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer text-gray-700"
            title="Título 2"
          >
            <i className="ri-h-2"></i>
          </button>
          <button
            type="button"
            onClick={() => formatHeading('h3')}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer text-gray-700"
            title="Título 3"
          >
            <i className="ri-h-3"></i>
          </button>
        </div>

        {/* Lists */}
        <div className="flex items-center gap-1 px-3 border-r border-gray-300">
          <button
            type="button"
            onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer text-gray-700"
            title="Lista com marcadores"
          >
            <i className="ri-list-unordered"></i>
          </button>
          <button
            type="button"
            onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer text-gray-700"
            title="Lista numerada"
          >
            <i className="ri-list-ordered"></i>
          </button>
        </div>

        {/* Quote & Image */}
        <div className="flex items-center gap-1 pl-3">
          <button
            type="button"
            onClick={() => formatQuote()}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer text-gray-700"
            title="Citação"
          >
            <i className="ri-double-quotes-l"></i>
          </button>
          <button
            type="button"
            onClick={() => setShowImageModal(true)}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer text-gray-700"
            title="Inserir Imagem"
          >
            <i className="ri-image-add-line"></i>
          </button>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Inserir Imagem</h3>
                <button
                  onClick={() => {
                    setShowImageModal(false);
                    setImageUrl('');
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fazer Upload</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file);
                  }}
                  className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-red-50 file:text-red-600 hover:file:bg-red-100 file:cursor-pointer cursor-pointer"
                  disabled={uploadingImage}
                />
                <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF ou WebP (máx. 5MB)</p>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-xs text-gray-500">ou</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              {/* URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">URL da Imagem</label>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://exemplo.com/imagem.jpg"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleInsertImageUrl();
                    }
                  }}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowImageModal(false);
                  setImageUrl('');
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleInsertImageUrl}
                disabled={!imageUrl.trim() || uploadingImage}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingImage ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    A carregar...
                  </>
                ) : (
                  'Inserir'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface InitialContentPluginProps {
  initialContent: string;
}

function InitialContentPlugin({ initialContent }: InitialContentPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (isFirstRender && initialContent) {
      setIsFirstRender(false);
      editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(initialContent, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);
        const root = $getRoot();
        root.clear();
        root.append(...nodes);
      });
    }
  }, [editor, initialContent, isFirstRender]);

  return null;
}

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Escreva o conteúdo do artigo...',
}: RichTextEditorProps) {
  const initialConfig = {
    namespace: 'BlogEditor',
    theme,
    onError: (error: Error) => console.error(error),
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, ImageNode],
  };

  const handleChange = (editorState: EditorState, editor: LexicalEditor) => {
    editorState.read(() => {
      const html = $generateHtmlFromNodes(editor);
      onChange(html);
    });
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-red-500 focus-within:border-transparent">
        <ToolbarPlugin />
        <div className="relative min-h-[300px]">
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="min-h-[300px] p-4 outline-none text-sm prose prose-sm max-w-none" />
            }
            placeholder={
              <div className="absolute top-4 left-4 text-gray-400 pointer-events-none text-sm">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <OnChangePlugin onChange={handleChange} />
          <InitialContentPlugin initialContent={value} />
          <ImagePlugin />
        </div>
      </div>
    </LexicalComposer>
  );
}
